import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchStaffMemberByIdQueryOptions } from "@/api/staff";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { StaffDetailsNavigation } from "@/routes/_auth/app/_app/org/$orgId/staff/-components/StaffDetailsNavigation";

const staffDetailSearchSchema = z.object({
	type: z.enum(["staff", "invitation"]).optional().default("staff"),
	email: z.string().optional(),
	displayName: z.string().optional(),
	role: z.string().optional(),
	expiresAt: z.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/staff/$staffId",
)({
	validateSearch: staffDetailSearchSchema,
	loaderDeps: ({ search }) => ({ type: search.type }),
	loader: async ({ context, params, deps }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, staffId } = params;

		// Only preload staff member data for actual staff (not invitations)
		if (deps.type !== "invitation") {
			context.queryClient.ensureQueryData(
				fetchStaffMemberByIdQueryOptions(userId, token, orgId, staffId),
			);
		}

		return { userId, token, orgId, staffId };
	},
	component: StaffDetailsLayout,
});

function StaffDetailsLayout() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const search = Route.useSearch();
	const location = useLocation();

	const isInvitation = search.type === "invitation";

	// Determine active tab based on current route
	const getActiveTab = ():
		| "overview"
		| "edit"
		| "permissions"
		| "schedules"
		| "migrate-schedules" => {
		const pathname = location.pathname;
		if (pathname.endsWith("/migrate-schedules")) return "migrate-schedules";
		if (pathname.endsWith("/schedules")) return "schedules";
		if (pathname.endsWith("/permissions")) return "permissions";
		if (pathname.endsWith("/edit")) return "edit";
		return "overview"; // default to overview
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("staff.navigation.title")}
				description={t("staff.navigation.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="staff-details-container"
			>
				{/* Side Navigation */}
				<StaffDetailsNavigation
					activeTab={getActiveTab()}
					userId={userId}
					token={token}
					isInvitation={isInvitation}
				/>

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="staff-details-main-content-area"
				>
					<Outlet />
				</div>
			</div>
		</div>
	);
}
